import { Injectable } from '@angular/core';
import { RestService } from './rest.service';
import { TranslationService } from './translation.service';
import { BrandService } from '../../services/brand-config.service';
import { SessionService } from './session.service';

export interface HandoffCreated {
  token: string;
  ttlSec: number;
  step: string;
  scanId: string | null;
  /** Full deep link the phone opens (built client-side; token only, no credential). */
  url: string;
}

export interface HandoffRedeemed {
  tokenHash?: string;
  scanId?: string | null;
  step?: string;
  error?: { message: string };
}

/**
 * Cross-device face-scan capture handoff (DEV_SPEC_desktop_capture_warning.md).
 * Desktop/tablet mints a one-time token; the phone opens the deep link, redeems
 * it for a Supabase magiclink token_hash and establishes the same session with
 * no re-login. The QR/link carry only the opaque token — never the credential.
 */
@Injectable({ providedIn: 'root' })
export class HandoffService {
  constructor(
    private rest: RestService,
    private translation: TranslationService,
    private brand: BrandService,
    private session: SessionService,
  ) {}

  /** No app session = guest funnel (nothing to hand off cross-device). */
  isGuest(): boolean {
    return !this.session.session?.token;
  }

  private brandName(): string {
    return this.brand.config.isMarkable ? 'markable' : 'peritale';
  }

  private lang(): string {
    return this.translation.getCurrentLanguage() === 'en' ? 'en' : 'he';
  }

  /** Mint a handoff token bound to the current session; returns the deep link. */
  async create(
    scanId: string | null,
    step = 'selfie',
  ): Promise<HandoffCreated | null> {
    const origin = window.location.origin;
    // Guest funnel: there is no session to carry to the phone, and the authed
    // handoff/create endpoint would 401 -> the global auth handler force-logs
    // her out to /login (the QR-screen "code error then bounced to login" bug
    // Danna reported in prod). Just point the QR at the same funnel on her
    // phone, where the camera opens normally (phones aren't gated). No server
    // call, no credential, no logout.
    if (this.isGuest()) {
      return {
        token: '',
        ttlSec: 0,
        step,
        scanId: scanId ?? null,
        url: `${origin}/${this.lang()}/app/test`,
      };
    }
    const res: any = await this.rest.request({
      path: 'handoff/create',
      method: 'POST',
      body: { brand: this.brandName(), scanId, step },
      throwError: false,
    });
    if (!res?.token) return null;
    const url = `${origin}/${this.lang()}/app/new-test?handoff=${res.token}`;
    return {
      token: res.token,
      ttlSec: res.ttlSec,
      step: res.step,
      scanId: res.scanId ?? null,
      url,
    };
  }

  /** Phone side: exchange the opaque token for a Supabase magiclink token_hash. */
  async redeem(token: string): Promise<HandoffRedeemed> {
    const res: any = await this.rest.request({
      path: 'handoff/redeem',
      method: 'POST',
      body: { token },
      throwError: false,
    });
    return (res || { error: { message: 'unknown' } }) as HandoffRedeemed;
  }

  /** Fallback layer: SMS the same deep link to a phone number she types. */
  async sendLink(
    phone: string,
    scanId: string | null,
    step = 'selfie',
  ): Promise<{ sent: boolean; provider?: string }> {
    const res: any = await this.rest.request({
      path: 'handoff/send-link',
      method: 'POST',
      body: {
        phone,
        brand: this.brandName(),
        scanId,
        step,
        origin: window.location.origin,
        lang: this.lang(),
      },
      throwError: false,
    });
    return { sent: !!res?.sent, provider: res?.provider };
  }
}
